import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button, Dialog, DialogContent, DialogTitle } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../App';

const X_LINK = 'https://xpainkiller.com/'

const AppViewPage = () => {
  const { projectName } = useParams();
  const navigate = useNavigate();
  const [htmlContent, setHtmlContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [communityLink, setCommunityLink] = useState(X_LINK);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const lastTokenRef = useRef(null);

  useEffect(() => {
    if (projectName) {
      loadApp(projectName);
    }
  }, [projectName]);

  useEffect(() => {
    const handleMessage = async (event) => {
      if (event.data.type === 'OPEN_LOGIN_MODAL') {
        setLoginModalOpen(true);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const loadApp = async (name) => {
    try {
      // 获取应用信息
      const { data: app, error: dbError } = await supabase
        .from('apps')
        .select('id, project_name, app_name, code_url, community_link')
        .eq('project_name', name)
        .single();

      if (dbError) throw dbError;
      if (!app) {
        setError('App not found');
        return;
      }

      if (app.community_link) {
        setCommunityLink(app.community_link);
      }

      // 下载HTML内容
      const { data: fileData, error: storageError } = await supabase.storage
        .from('code')
        .download(app.code_url);

      if (storageError) throw storageError;

      const htmlText = await fileData.text();
      setHtmlContent(htmlText);
    } catch (error) {
      console.error('Error loading app:', error);
      setError('Failed to load app');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (provider) => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: provider,
        options: {
          redirectTo: window.location.href
        }
      });
      
      if (error) throw error;
      
      // OAuth will redirect, so we'll handle success in useEffect
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  const handleLoginSuccess = async (session) => {
    setLoginModalOpen(false);
    
    // Send session to iframe
    const iframe = document.querySelector('iframe');
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage({
        type: 'AUTH_UPDATE',
        session: session
      }, '*');
    }
  };

  // Check for auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.access_token) {
        // Deduplicate by access_token to avoid postMessage loops
        if (session.access_token === lastTokenRef.current) return;
        lastTokenRef.current = session.access_token;
        handleLoginSuccess(session);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100%',
        background: 'linear-gradient(135deg, #0f0f0f 0%, #1a1a2e 100%)',
        overflow: 'hidden'
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: '#6366f1', mb: 2 }} />
          <Typography sx={{ color: '#9ca3af' }}>Loading app...</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100%',
        background: 'linear-gradient(135deg, #0f0f0f 0%, #1a1a2e 100%)',
        padding: 2,
        textAlign: 'center',
        overflow: 'hidden'
      }}>
        <Typography variant="h4" sx={{ color: 'white' }} gutterBottom>
          {error}
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          sx={{
            mt: 2,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            color: 'white',
            '&:hover': {
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)'
            }
          }}
        >
          Back to Home
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      height: '100dvh',
      width: '100%',
      background: '#000',
      overflow: 'hidden'
    }}>
      {/* iframe占据剩余空间 */}
      <Box sx={{ 
        flex: 1,
        overflow: 'hidden'
      }}>
        <iframe
          srcDoc={htmlContent}
          style={{ 
            width: '100%', 
            height: '100%',
            border: 'none',
            display: 'block'
          }}
          title={`App ${projectName}`}
        />
      </Box>

      {/* Login Modal */}
      <Dialog
        open={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            background: '#ffffff',
            borderRadius: '16px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)'
          }
        }}
      >
        <DialogTitle sx={{
          textAlign: 'center',
          color: '#18181b',
          pb: 0.5,
          pt: 3,
          fontSize: '18px',
          fontWeight: 600
        }}>
          Login to Continue
        </DialogTitle>
        <DialogContent sx={{ pb: 4, pt: 1 }}>
          {/* <Typography sx={{
            color: '#71717a',
            textAlign: 'center',
            mb: 3,
            fontSize: '14px'
          }}>
            Connect your X account to continue
          </Typography> */}

          {/* Login with X */}
          <Button
            fullWidth
            onClick={() => handleLogin('twitter')}
            sx={{
              py: 1.5,
              background: '#18181b',
              color: '#ffffff',
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              '&:hover': {
                background: '#27272a'
              }
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            Continue with X
          </Button>

          {/* Login with Google */}
          <Button
            fullWidth
            onClick={() => handleLogin('google')}
            sx={{
              mt: 1.5,
              py: 1.5,
              background: '#ffffff',
              color: '#18181b',
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              border: '1px solid #e4e4e7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              '&:hover': {
                background: '#f4f4f5'
              }
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          {/* Login with Discord */}
          <Button
            fullWidth
            onClick={() => handleLogin('discord')}
            sx={{
              mt: 1.5,
              py: 1.5,
              background: '#5865F2',
              color: '#ffffff',
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              '&:hover': {
                background: '#4752C4'
              }
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
            </svg>
            Continue with Discord
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AppViewPage;
